import React, { useEffect, useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import SlideBase from './SlideBase'
import FormatCurrency from 'components/Common/FormatCurrency/FormatCurrency'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import FadeBackground from '../FadeBackground'
import { useWrappedStorySelector } from 'contexts/Wrapped'
import { WRAPPED_SLIDES } from '../../config/slides'
import useVideoBackgroundWithFallback from '../../hooks/useVideoBackgroundWithFallback'

// Get slide config for this slide (static, doesn't depend on runtime data)
const SLIDE_CONFIG = WRAPPED_SLIDES.find(s => s.id === 'savings')
const SLIDE_ID = 'savings' as const

function SavingsSlide() {
  const intl = useIntl()
  // Use granular selectors to minimize re-renders
  const data = useWrappedStorySelector(state => state.data)
  const totalsData = useWrappedStorySelector(state => state.totalsData)

  // Use the video background with fallback hook
  const { background, isBackgroundReady, isCurrentSlide } = useVideoBackgroundWithFallback({
    slideConfig: SLIDE_CONFIG,
    slideId: SLIDE_ID,
  })

  const [animate, setAnimate] = useState(!SLIDE_CONFIG?.video) // If no video, animate immediately

  // Totals-only mode is determined by not having user wrapped data
  const isTotalsOnlyMode = !data

  // Start animation when background is ready (video loaded or fallback image)
  useEffect(() => {
    if (SLIDE_CONFIG?.video && isBackgroundReady && isCurrentSlide) {
      setAnimate(true)
    }
  }, [isBackgroundReady, isCurrentSlide])

  // Need either user data or totals data to render
  if (!data && !totalsData) return null

  const userSavings = data?.stats.totalSavings ?? 0
  const userCurrency = data?.stats.currency ?? 'AUD'
  const totalsSavings = totalsData?.stats.totalSavings ?? 0
  const totalsCurrency = totalsData?.stats.currency ?? userCurrency
  const isLowSavings = userSavings < 100
  const useTotalsFallback = isTotalsOnlyMode || isLowSavings

  const savingsValue = useTotalsFallback ? totalsSavings : userSavings
  const savingsCurrency = useTotalsFallback ? totalsCurrency : userCurrency
  const useCompactFormat = savingsValue >= 1_000_000
  const dailyTotalSavings = totalsSavings / 365

  // Extract ternary heading variants as constants before return
  const headingTotalsFallback = intl.formatMessage({
    id: 'muVGIOtCAg',
    defaultMessage: 'In 2025, Luxury Escapes travellers saved a total of',
    description: 'Savings slide heading for totals fallback mode in the year-in-review feature',
  })
  const headingUserSavings = intl.formatMessage({
    id: 'q3zPfyhwQm',
    defaultMessage: 'With Luxury Escapes, you saved a total of',
    description: 'Savings slide heading for user savings in the year-end travel wrapped summary',
  })
  const heading = useTotalsFallback ? headingTotalsFallback : headingUserSavings

  const captionText = intl.formatMessage({
    id: 'AadD5UOjPY',
    defaultMessage: 'Enjoying More For Less',
    description: 'Caption for the savings statistic slide in the year-end travel wrapped summary',
  })

  const bodyTextNextEscape = intl.formatMessage({
    id: 'VfJHSEH5+Q',
    defaultMessage: 'And your next escape could save you even more.',
    description: 'Savings slide body text about future savings in the wrapped summary',
  })

  return (
    <SlideBase background={background}>
      {animate && <FadeBackground>
        <Group direction="vertical" grow verticalAlign="end" horizontalAlign="start" gap={12}>
          <Group direction="vertical" gap={4}>
            <Caption colour="highlight-primary" variant="small" format="uppercase">{captionText}</Caption>
            <Heading variant="heading5">
              <b>
                {heading}
              </b>
            </Heading>
          </Group>
          <Group direction="vertical" gap={4}>
            <Heading variant="heading1alt">
              <FormatCurrency value={savingsValue} currency={savingsCurrency} notation={useCompactFormat ? 'compact' : 'standard'} maximumFractionDigits={0} />
            </Heading>
            {!useTotalsFallback && (
              <BodyText variant="medium">
                {bodyTextNextEscape}
              </BodyText>
            )}
            {useTotalsFallback && (
              <BodyText variant="medium">
                <FormattedMessage
                  id="FwYOjdlBMJ"
                  defaultMessage="That’s enough to save {formatCurrency} every day, all year long."
                  description="Savings slide body text about daily savings amount with FormatCurrency component"
                  values={{
                    formatCurrency: <FormatCurrency value={dailyTotalSavings} currency={savingsCurrency} notation="compact" maximumFractionDigits={1} />,
                  }}
                />
              </BodyText>
            )}
          </Group>
        </Group>
      </FadeBackground>}
    </SlideBase>
  )
}

export default React.memo(SavingsSlide)
